import React, { useState, useEffect, useRef } from "react";
import {
  FileText, CheckCircle2, AlertCircle, Loader2,
  ChevronDown, ChevronUp, Send, ExternalLink, X, RotateCcw,
} from "lucide-react";
import CandidatePreview, { mapToForm } from "./CandidatePreview";
import { useParseHistory } from "../hooks/useParseHistory";

const CONCURRENCY = 5;

function initItem(file, id) {
  return {
    id,
    file,
    status: "pending", // pending | parsing | done | error
    progress: 0,
    preview: null,   // { parsed, filename }
    editedForm: null,
    error: null,
    checked: true,
    expanded: false,
    zohoStatus: "idle", // idle | submitting | success | error
    zohoResult: null,
    zohoError: null,
  };
}

export default function BatchUploadPanel({ files, onReset }) {
  const [items, setItems] = useState(() => files.map((f, i) => initItem(f, i)));
  const [sessionExpired, setSessionExpired] = useState(false);
  const { addEntry } = useParseHistory();
  const idxRef = useRef(0);

  // Patch a single item by id
  const patch = (id, updates) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...updates } : it)));

  // Worker-pool parallel parsing
  useEffect(() => {
    idxRef.current = 0;

    async function parseOne(id, file) {
      patch(id, { status: "parsing" });
      try {
        const fd = new FormData();
        fd.append("file", file);

        let prog = 0;
        const ticker = setInterval(() => {
          prog = Math.min(prog + 8, 85);
          patch(id, { progress: prog });
        }, 300);

        const res = await fetch("/api/parse-preview", { method: "POST", body: fd, headers: { "X-Session-ID": localStorage.getItem("session_id") || "" } });
        clearInterval(ticker);

        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          throw new Error(detail?.detail || `HTTP ${res.status}`);
        }

        const data = await res.json();
        patch(id, {
          status: "done",
          progress: 100,
          preview: data,
          editedForm: mapToForm(data.parsed),
        });
      } catch (err) {
        patch(id, { status: "error", progress: 0, error: err.message });
      }
    }

    async function worker() {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const id = idxRef.current++;
        if (id >= files.length) break;
        await parseOne(id, files[id]);
      }
    }

    const workers = Array.from(
      { length: Math.min(CONCURRENCY, files.length) },
      worker
    );
    Promise.all(workers);
  }, []); // run once on mount

  const handleSendSelected = async () => {
    const toSend = items.filter(
      (it) => it.checked && it.status === "done" && it.zohoStatus === "idle"
    );

    for (const item of toSend) {
      patch(item.id, { zohoStatus: "submitting" });
      try {
        const fd = new FormData();
        fd.append("candidate_json", JSON.stringify(item.editedForm));
        fd.append("filename", item.preview.filename);
        fd.append("file", item.file);

        const res = await fetch("/api/push-to-zoho", { method: "POST", body: fd, headers: { "X-Session-ID": localStorage.getItem("session_id") || "" } });
        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          throw new Error(detail?.detail || `HTTP ${res.status}`);
        }

        const data = await res.json();
        patch(item.id, { zohoStatus: "success", zohoResult: data });
        addEntry(item.editedForm, item.preview.filename);
      } catch (err) {
        const msg = err.message || "";
        if (/INVALID_TOKEN|invalid oauth token/i.test(msg)) {
          setSessionExpired(true);
          patch(item.id, { zohoStatus: "idle" });
          break;
        }
        patch(item.id, { zohoStatus: "error", zohoError: msg });
      }
    }
  };

  // Derived counts
  const doneItems = items.filter((it) => it.status === "done");
  const parsedCount = doneItems.length;
  const errorCount = items.filter((it) => it.status === "error").length;
  const pendingCount = items.filter(
    (it) => it.status === "parsing" || it.status === "pending"
  ).length;
  const eligibleCount = items.filter(
    (it) => it.checked && it.status === "done" && it.zohoStatus === "idle"
  ).length;
  const isSending = items.some((it) => it.zohoStatus === "submitting");
  const sentCount = items.filter((it) => it.zohoStatus === "success").length;
  const allDoneChecked =
    doneItems.length > 0 &&
    doneItems.filter((it) => it.zohoStatus === "idle").every((it) => it.checked);

  const toggleAll = () => {
    const newVal = !allDoneChecked;
    setItems((prev) =>
      prev.map((it) =>
        it.status === "done" && it.zohoStatus === "idle"
          ? { ...it, checked: newVal }
          : it
      )
    );
  };

  return (
    <div>
      {/* Session expired banner */}
      {sessionExpired && (
        <div style={{
          marginBottom: 16, padding: "12px 14px",
          background: "rgba(248,113,113,0.08)",
          border: "1px solid rgba(248,113,113,0.2)",
          borderRadius: 8, display: "flex", alignItems: "center", gap: 10,
        }}>
          <AlertCircle size={15} color="#F87171" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: "#F87171", fontWeight: 500, marginBottom: 6 }}>
              Сессия истекла. Пожалуйста, войдите заново.
            </div>
            <a
              href="https://recruitparser.be-exec.com/auth/login"
              style={{
                display: "inline-block", padding: "6px 14px",
                background: "rgba(248,113,113,0.15)",
                border: "1px solid rgba(248,113,113,0.4)",
                borderRadius: 6, fontSize: 12, color: "#F87171",
                textDecoration: "none", fontWeight: 600,
              }}
            >
              Войти снова
            </a>
          </div>
        </div>
      )}

      {/* Summary bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 20,
          padding: "10px 16px",
          background: "var(--ink-800)",
          border: "1px solid var(--slate-border)",
          borderRadius: 10,
        }}
      >
        <span
          style={{
            fontFamily: "Syne, sans-serif",
            fontWeight: 700,
            fontSize: 13,
            color: "#E8EDF3",
          }}
        >
          Batch mode
        </span>

        <span
          style={{
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 11,
            color: "var(--slate-muted)",
          }}
        >
          {files.length} файл{files.length > 4 ? "ов" : files.length > 1 ? "а" : ""}
        </span>

        {pendingCount > 0 && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: "var(--acid)",
              fontFamily: "JetBrains Mono, monospace",
            }}
          >
            <Loader2 size={10} style={{ animation: "spin 1s linear infinite" }} />
            парсинг {pendingCount}...
          </span>
        )}

        {parsedCount > 0 && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: "#4ADE80",
              fontFamily: "JetBrains Mono, monospace",
            }}
          >
            <CheckCircle2 size={10} />
            {parsedCount} готово
          </span>
        )}

        {errorCount > 0 && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: "#F87171",
              fontFamily: "JetBrains Mono, monospace",
            }}
          >
            <AlertCircle size={10} />
            {errorCount} ошибок
          </span>
        )}

        {sentCount > 0 && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: "#4ADE80",
              fontFamily: "JetBrains Mono, monospace",
            }}
          >
            <CheckCircle2 size={10} />
            {sentCount} отправлено в Zoho
          </span>
        )}

        {/* Right side controls */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {doneItems.filter((it) => it.zohoStatus === "idle").length > 0 && (
            <button
              onClick={toggleAll}
              style={{
                background: "none",
                border: "1px solid var(--slate-border)",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                color: "var(--slate-text)",
                cursor: "pointer",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {allDoneChecked ? "Снять все" : "Выбрать все"}
            </button>
          )}

          <button
            onClick={onReset}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "none",
              border: "none",
              padding: "4px 6px",
              fontSize: 11,
              color: "var(--slate-muted)",
              cursor: "pointer",
            }}
          >
            <X size={12} />
            Сбросить
          </button>
        </div>
      </div>

      {/* Items list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24 }}>
        {items.map((item) => (
          <BatchItem
            key={item.id}
            item={item}
            onToggleCheck={() =>
              patch(item.id, { checked: !item.checked })
            }
            onToggleExpand={() =>
              patch(item.id, { expanded: !item.expanded })
            }
            onSaveForm={(form) =>
              patch(item.id, { editedForm: form, expanded: false })
            }
            onCollapseOnly={() =>
              patch(item.id, { expanded: false })
            }
          />
        ))}
      </div>

      {/* Sticky send button */}
      {eligibleCount > 0 && (
        <div
          style={{
            position: "sticky",
            bottom: 24,
            display: "flex",
            justifyContent: "flex-end",
            pointerEvents: "none",
          }}
        >
          <button
            onClick={handleSendSelected}
            disabled={isSending}
            style={{
              pointerEvents: "all",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 24px",
              background: isSending ? "var(--ink-700)" : "var(--acid)",
              border: isSending
                ? "1px solid var(--slate-border)"
                : "none",
              borderRadius: 10,
              fontFamily: "Syne, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              color: isSending ? "var(--slate-text)" : "var(--ink-950)",
              cursor: isSending ? "not-allowed" : "pointer",
              boxShadow: isSending
                ? "none"
                : "0 4px 24px rgba(200,241,53,0.25)",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              if (!isSending) {
                e.currentTarget.style.background = "var(--acid-glow)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSending) {
                e.currentTarget.style.background = "var(--acid)";
                e.currentTarget.style.transform = "translateY(0)";
              }
            }}
          >
            {isSending ? (
              <>
                <Loader2 size={15} style={{ animation: "spin 1s linear infinite" }} />
                Отправка…
              </>
            ) : (
              <>
                <Send size={15} strokeWidth={2} />
                Отправить выбранные ({eligibleCount}) в Zoho
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function BatchItem({ item, onToggleCheck, onToggleExpand, onSaveForm, onCollapseOnly }) {
  const {
    file,
    status,
    progress,
    preview,
    editedForm,
    error,
    checked,
    expanded,
    zohoStatus,
    zohoResult,
    zohoError,
  } = item;

  const parsedName = editedForm
    ? [editedForm.First_Name, editedForm.Last_Name].filter(Boolean).join(" ") || null
    : null;
  const parsedTitle = editedForm
    ? [editedForm.Current_Job_Title, editedForm.Current_Employer]
        .filter(Boolean)
        .join(" @ ")
    : null;

  const isSelectable = status === "done" && zohoStatus === "idle";
  const isSent = zohoStatus === "success";
  const isSendError = zohoStatus === "error";
  const isSubmitting = zohoStatus === "submitting";

  return (
    <div
      style={{
        border: `1px solid ${
          isSent
            ? "rgba(74,222,128,0.25)"
            : isSendError
            ? "rgba(248,113,113,0.25)"
            : "var(--slate-border)"
        }`,
        borderRadius: 10,
        overflow: "hidden",
        background: isSent ? "rgba(74,222,128,0.03)" : "var(--ink-800)",
        transition: "border-color 0.2s ease",
      }}
    >
      {/* Row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "11px 14px",
          minHeight: 56,
        }}
      >
        {/* Left: checkbox / status icon */}
        <div
          style={{
            flexShrink: 0,
            width: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSubmitting ? (
            <Loader2
              size={15}
              color="var(--acid)"
              style={{ animation: "spin 1s linear infinite" }}
            />
          ) : isSent ? (
            <CheckCircle2 size={15} color="#4ADE80" />
          ) : status === "error" ? (
            <AlertCircle size={15} color="#F87171" />
          ) : status === "parsing" ? (
            <div
              style={{
                width: 15,
                height: 15,
                borderRadius: "50%",
                border: "2px solid var(--slate-border)",
                borderTopColor: "var(--acid)",
                animation: "spin 0.9s linear infinite",
              }}
            />
          ) : status === "pending" ? (
            <div
              style={{
                width: 15,
                height: 15,
                borderRadius: "50%",
                border: "2px solid var(--slate-border)",
              }}
            />
          ) : (
            <input
              type="checkbox"
              checked={checked}
              onChange={onToggleCheck}
              disabled={!isSelectable}
              style={{
                width: 15,
                height: 15,
                accentColor: "var(--acid)",
                cursor: isSelectable ? "pointer" : "default",
              }}
            />
          )}
        </div>

        {/* File icon */}
        <div
          style={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 7,
            background: "rgba(200,241,53,0.06)",
            border: "1px solid rgba(200,241,53,0.13)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FileText size={14} color="var(--acid)" />
        </div>

        {/* Name / file info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {parsedName ? (
            <>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#E8EDF3",
                  marginBottom: 2,
                  fontFamily: "Syne, sans-serif",
                }}
              >
                {parsedName}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--slate-muted)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {parsedTitle && (
                  <span style={{ color: "var(--slate-text)" }}>{parsedTitle} · </span>
                )}
                {file.name}
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: status === "error" ? "#F87171" : "#E8EDF3",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  marginBottom: status === "parsing" ? 4 : 0,
                }}
              >
                {file.name}
              </div>
              {status === "parsing" && (
                <div
                  style={{
                    height: 2,
                    background: "var(--ink-700)",
                    borderRadius: 999,
                    overflow: "hidden",
                    maxWidth: 180,
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${progress}%`,
                      background: "var(--acid)",
                      borderRadius: 999,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              )}
              {status === "error" && (
                <div style={{ fontSize: 11, color: "#F87171", marginTop: 2 }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Right badges / actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {isSent && (
            <span
              style={{
                fontSize: 10,
                color: "#4ADE80",
                fontFamily: "JetBrains Mono, monospace",
              }}
            >
              ✓ sent
            </span>
          )}

          {isSent && zohoResult?.zoho_url && (
            <a
              href={zohoResult.zoho_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                color: "var(--acid)",
                textDecoration: "none",
                border: "1px solid rgba(200,241,53,0.25)",
                borderRadius: 6,
                padding: "3px 8px",
              }}
            >
              <ExternalLink size={10} />
              Zoho
            </a>
          )}

          {isSendError && (
            <span
              style={{
                fontSize: 11,
                color: "#F87171",
                background: "rgba(248,113,113,0.08)",
                border: "1px solid rgba(248,113,113,0.2)",
                borderRadius: 6,
                padding: "3px 8px",
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={zohoError}
            >
              {zohoError}
            </span>
          )}

          {status === "done" && !isSent && (
            <button
              onClick={onToggleExpand}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                background: expanded ? "rgba(200,241,53,0.08)" : "none",
                border: `1px solid ${expanded ? "rgba(200,241,53,0.25)" : "var(--slate-border)"}`,
                borderRadius: 6,
                padding: "5px 10px",
                fontSize: 11,
                color: expanded ? "var(--acid)" : "var(--slate-text)",
                cursor: "pointer",
                transition: "all 0.15s ease",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {expanded ? (
                <>
                  <ChevronUp size={12} />
                  Свернуть
                </>
              ) : (
                <>
                  <ChevronDown size={12} />
                  Редактировать
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Expanded inline editor */}
      {expanded && preview && (
        <div style={{ borderTop: "1px solid var(--slate-border)" }}>
          <CandidatePreview
            data={preview.parsed}
            initialForm={editedForm}
            filename={preview.filename}
            confirmLabel="✓ Сохранить изменения"
            showHeader={false}
            onConfirm={onSaveForm}
            onCancel={onCollapseOnly}
            loading={false}
          />
        </div>
      )}
    </div>
  );
}
